/**
 *
 * @module mediaClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/playback-session.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/playhead.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 * @see https://github.bamtech.co/skynet/media-service/blob/master/docs/api/README.md
 * @see https://github.bamtech.co/skynet/playback-orchestration-service/blob/master/docs/api/WEB.md
 * @see https://github.bamtech.co/services-commons/public-api/blob/master/swagger/services/thumbnail.md
 * @see https://github.bamtech.co/skynet/playback-orchestration-service/blob/master/public/swagger-2.0/swagger.json
 *
 */

/* eslint-disable quote-props */

import Check from 'check-types';
import Logging from './../../logging/entry';
import HttpClient from './../platforms/httpClient';
import Util from './../util/entry';
import Token from './../token/entry';
import Exception from './../exception/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import MediaPayload from './mediaPayload';
import MediaPlayhead from './mediaPlayhead';
import MediaPlayheadStatus from './mediaPlayheadStatus';
import MediaClientEndpoint from './mediaClientEndpoint';
import MediaClientConfiguration from './mediaClientConfiguration';
import MediaThumbnailLink from './mediaThumbnailLink';
import MediaThumbnailLinks from './mediaThumbnailLinks';

import SpriteThumbnailSet from './spriteThumbnailSet';
import BifThumbnailSet from './bifThumbnailSet';
import Presentation from './presentation';

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const MediaClientDustUrnReference = DustUrnReference.services.media.mediaClient;

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;
const ExceptionReference = Exception.ExceptionReference;

const {
    nonEmptyString,
    instanceStrict,
    nonEmptyObject,
    number
} = Check.assert;

/**
 *
 * @class MediaClient
 * @implements {IMediaClient}
 * @desc Provides a data client that can be used to access media services.
 *
 */
export default class MediaClient {

    /**
     *
     * @param {MediaClientConfiguration} mediaClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(mediaClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'MediaClient(mediaClientConfiguration, logger, httpClient)',
            get mediaClientConfiguration() {
                return `${this.ctor} needs MediaClientConfiguration instance`;
            }
        };

        instanceStrict(
            mediaClientConfiguration,
            MediaClientConfiguration,
            errorMessage.mediaClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {MediaClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = mediaClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(MediaClient.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {String} mediaLocation - The absolute URL of the playlist service request.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @param {String} playbackScenario - The playback scenario used to construct the service request.
     * @desc Retrieves the MediaPayload from the media location using the supplied playback scenario.
     * @note we remap several items from the playlist service before constructing `SDK.Services.Media.MediaPayload`
     * @returns {Promise<MediaPayload>}
     *
     */
    getMediaPayload(mediaLocation, accessToken, playbackScenario) {

        const errorMessage = {
            method: 'MediaClient.getMediaPayload(mediaLocation, accessToken, playbackScenario)',
            get mediaLocation() {
                return `${this.method} mediaLocation should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            },
            get playbackScenario() {
                return `${this.method} playbackScenario should be a String with length`;
            }
        };

        nonEmptyString(mediaLocation, errorMessage.mediaLocation, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);
        nonEmptyString(playbackScenario, errorMessage.playbackScenario, InvalidArgumentException);

        const { dustEnabled, httpClient, logger, getMediaPlayhead } = this;

        const payload = this.getPayload(
            accessToken,
            MediaClientEndpoint.mediaPayload,
            mediaLocation,
            playbackScenario
        );

        logger.info(MediaClient.name, `Sending get media payload request: ${payload.url}`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaClient.name,
            urn: MediaClientDustUrnReference.getMediaPayload,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { stream, tracking, playhead, thumbnails } = data || {};

            const complete = stream.complete || stream.location || null;
            const slide = stream.slide || null;
            const attributes = stream.attributes || {};
            const mediaPlayhead = getMediaPlayhead(playhead);

            let mediaThumbnailLinks;

            if (Check.assigned(thumbnails)) {
                mediaThumbnailLinks = new MediaThumbnailLinks(thumbnails);
            }

            logger.info(MediaClient.name, `Received get media payload response: ${complete}`);

            const options = {
                completeStreamUri: complete,
                slideStreamUri: slide,
                trackingInfo: tracking,
                playhead: mediaPlayhead,
                attributes,
                thumbnails: mediaThumbnailLinks
            };

            return Promise.resolve(new MediaPayload(options));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Creates a cookie based on the accessToken
     * @returns {Promise<Void>}
     *
     */
    createAuthCookie(accessToken) {

        const errorMessage = {
            method: 'MediaClient.createAuthCookie(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            MediaClientEndpoint.playbackCookie
        );

        logger.info(MediaClient.name, `Sending create auth cookie request: ${payload.url}`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaClient.name,
            urn: MediaClientDustUrnReference.createAuthCookie,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {Object} options
     * @param {SDK.Services.Media.ThumbnailLink} options.thumbnailLink
     * @param {?String} [options.resolution]
     * @param {SDK.Services.Token.AccessToken} options.accessToken
     * @note overwrites each `presentation` field returned from the service after converting that `Object` into
     * an instance of `SDK.Services.Media.Presentation`
     * @returns {Promise<Array<SDK.Services.Media.SpriteThumbnailSet>>}
     *
     */
    getSpriteSheetThumbnails(options) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnail(options)`,
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get thumbnailLink() {
                return `${this.method} options.thumbnailLink needs SDK.Services.Media.ThumbnailLink instance`;
            },
            get resolution() {
                return `${this.method} options.resolution should be a String with length`;
            },
            get accessToken() {
                return `${this.method} options.accessToken needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { thumbnailLink, resolution, accessToken } = options;

        instanceStrict(thumbnailLink, MediaThumbnailLink, errorMessage.thumbnailLink, InvalidArgumentException);

        if (Check.assigned(resolution)) {
            nonEmptyString(resolution, errorMessage.resolution, InvalidArgumentException);
        }

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { method } = thumbnailLink;
        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            MediaClientEndpoint.spriteSheetThumbnails,
            thumbnailLink,
            resolution
        );

        logger.info(MediaClient.name, `request spritesheet thumbnails ${thumbnailLink.url}`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaClient.name,
            urn: MediaClientDustUrnReference.getSpriteSheetThumbnails,
            payload,
            method
        });

        return httpClient[thumbnailLink.method.toLowerCase()](payload).catch((ex) => {

            throw new ServiceException('', [], ex);

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'getSpriteSheetThumbnails');

        }).then((response) => {

            const { data } = response;
            const { spritesheets } = data;

            if (Check.not.nonEmptyArray(spritesheets)) {

                const thumbnailsNotAvailableException = new ServiceException({
                    exceptionData: ExceptionReference.media.thumbnailsNotAvailable
                });

                return Promise.reject(thumbnailsNotAvailableException);

            }

            const spriteThumbnailSets = spritesheets.map((spritesheet) => {

                const presentations = spritesheet.presentations.map(
                    (presentation) => new Presentation(presentation)
                );

                return new SpriteThumbnailSet(Object.assign({}, spritesheet, { presentations }));

            });

            return Promise.resolve(spriteThumbnailSets);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Services.Media.Presentation} presentation
     * @param {Number} index
     * @note fetches the Byte[] version of the .jpeg file - unlikely this will be used on JS platforms as it generally
     * means saving the byte array to disk and loading into memory later on
     * @returns {Promise<Array<Byte>>}
     *
     */
    getSpriteSheetThumbnail(presentation, index) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnail(presentation, index)`,
            get presentation() {
                return `${this.method} presentation needs SDK.Services.Media.Presentation instance`;
            },
            get index() {
                return `${this.method} index should be a Number`;
            }
        };

        instanceStrict(presentation, Presentation, errorMessage.presentation, InvalidArgumentException);
        number(index, errorMessage.index, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            null, // we do not use an accessToken for this request
            MediaClientEndpoint.spriteSheetThumbnail,
            presentation.paths[index]
        );

        logger.info(MediaClient.name, `request spritesheet thumbnail ${payload.url}`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaClient.name,
            urn: MediaClientDustUrnReference.getSpriteSheetThumbnail,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((ex) => {

            throw new ServiceException('', [], ex);

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'getSpriteSheetThumbnail');

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {Object} options
     * @param {SDK.Services.Media.MediaThumbnailLink} options.thumbnailLink
     * @param {?String} [options.resolution]
     * @param {SDK.Services.Token.AccessToken} options.accessToken
     * @note overwrites each `presentation` field returned from the service after converting that `Object` into
     * an instance of `SDK.Services.Media.Presentation`
     * @returns {Promise<Array<SDK.Services.Media.BifThumbnailSet>>}
     *
     */
    getBifThumbnails(options) {

        const errorMessage = {
            method: `${this.constructor.name}.getBifThumbnails(options)`,
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get thumbnailLink() {
                return `${this.method} options.thumbnailLink needs SDK.Services.Media.ThumbnailLink instance`;
            },
            get resolution() {
                return `${this.method} options.resolution should be a String with length`;
            },
            get accessToken() {
                return `${this.method} options.accessToken needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { thumbnailLink, resolution, accessToken } = options;

        instanceStrict(thumbnailLink, MediaThumbnailLink, errorMessage.mediaThumbnailLink, InvalidArgumentException);

        if (Check.assigned(resolution)) {
            nonEmptyString(resolution, errorMessage.resolution, InvalidArgumentException);
        }

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { method } = thumbnailLink;
        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            MediaClientEndpoint.bifThumbnails,
            thumbnailLink,
            resolution
        );

        logger.info(MediaClient.name, `request bif thumbnails ${thumbnailLink.url}`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: MediaClient.name,
            urn: MediaClientDustUrnReference.getBifThumbnails,
            payload,
            method
        });

        return httpClient[thumbnailLink.method.toLowerCase()](payload).catch((ex) => {

            throw new ServiceException('', [], ex);

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'getBifThumbnails');

        }).then((response) => {

            const { data } = response;
            const { bifs } = data;

            if (Check.not.nonEmptyArray(bifs)) {

                const thumbnailsNotAvailableException = new ServiceException({
                    exceptionData: ExceptionReference.media.thumbnailsNotAvailable
                });

                return Promise.reject(thumbnailsNotAvailableException);

            }

            const bifThumbnailSets = bifs.map((bif) => {

                const presentations = bif.presentations.map(
                    (presentation) => new Presentation(presentation)
                );

                return new BifThumbnailSet(Object.assign({}, bif, { presentations }));

            });

            return Promise.resolve(bifThumbnailSets);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @note the JS SDK does not support this method
     * @returns {Promise<Void>}
     *
     */
    downloadBifThumbnail() {

        const errorMessage = {
            method: `${this.constructor.name}.downloadBifThumbnail()`
        };

        return new Promise((resolve) => {
            return resolve(new Error(`${errorMessage.method} - not-implemented`));
        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {Object} [data={}] - playhead data object
     * @desc Constructs a MediaPlayhead instance based on the provided playhead data, returns null
     * if the data is not available. Please note, this method checks against the status property because
     * its guaranteed to exist if the playhead exists.
     * @returns {MediaPlayhead|Null}
     * @see https://github.bamtech.co/skynet/playback-orchestration-service/blob/master/docs/api/WEB.md
     *
     */
    getMediaPlayhead(data = {}) {

        const { position, last_updated, status } = data; // eslint-disable-line

        if (Check.assigned(status)) {

            const remap = {
                PlayheadFound: MediaPlayheadStatus.Success
            };

            const playheadStatus = MediaPlayheadStatus[status] || remap[status] || MediaPlayheadStatus.Unavailable;

            return new MediaPlayhead(position, last_updated, playheadStatus);

        }

        return null;

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {MediaClientEndpoint} rel - endpoint type
     * @param {String} href - endpoint url template
     * @param {String} param - replaces template value in href
     * @note some methods pass in `SDK.Services.Media.MediaThumbnailLink` as the `href` argument - this will be parsed
     * to construct the correct url and query string used when fetching thumbnails
     * @note `spriteSheetThumbnail` does not need an `Authorization` header
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, href, param) {

        const { endpoints } = this.config;

        const endpoint = endpoints[rel];

        let headers = endpoint.headers;
        let url = href;
        let credentials;
        let bodyType;

        if (rel === 'playbackCookie') {
            url = endpoint.href;
            credentials = 'include';
        }

        if (rel === 'mediaPayload') {
            url = url.replace(/\{scenario\}/, param);
        }
        if (rel === 'spriteSheetThumbnails' || rel === 'bifThumbnails') {

            const queryChar = url.href.includes('?') ? '&' : '?';
            const resolution = param ? `resolution=${param}` : null;
            const queryString = resolution ? `${queryChar}${resolution}` : '';

            url = `${url.href}${queryString}`;
            headers = Object.assign({}, href.headers, headers);

        }

        if (accessToken) {
            headers.Authorization = accessToken.token;
        }

        if (rel === 'spriteSheetThumbnail') {

            delete headers.Authorization;

            bodyType = 'arrayBuffer';

        }

        return {
            url,
            headers,
            bodyType,
            credentials
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/media/mediaClient.js